import { CLIENT_BATCH_MS, MAX_BATCH, encodePops, parseServerMsg } from "../../shared/protocol";
import { usePopStore } from "../state/usePopStore";
import { dbg } from "../lib/debug";
import { getCount, postPops } from "./httpFallback";

// Client side of the global pop counter.
// Design rules (mirrors the plan):
// - pops are batched: at most one WS message per 500ms
// - reconnect backoff 1→2→4→8→15→30s with ±30% jitter; instant retry on
//   visibilitychange/online
// - after 2 consecutive WS failures, HTTP polling keeps the number moving
//   while WS retries continue in the background
// - the 3D experience never depends on any of this — worst case the number
//   freezes and pops stay local

const BACKOFF_S = [1, 2, 4, 8, 15, 30];
const KEEPALIVE_MS = 30_000;
const MAX_OFFLINE_BUFFER = 500;
const FALLBACK_POST_MS = 3_000;
const FALLBACK_POLL_MS = 10_000;

type MilestoneListener = (threshold: number, total: number) => void;
const milestoneListeners = new Set<MilestoneListener>();

/**
 * Fires when the server announces the Grand Tally crossing a threshold.
 * Everyone connected receives it in the same instant — that simultaneity is
 * the whole point, so this is never replayed on reconnect.
 */
export function onMilestone(fn: MilestoneListener): () => void {
  milestoneListeners.add(fn);
  return () => milestoneListeners.delete(fn);
}

/** One country's pops since the last broadcast, busiest first. */
export type GeoPulse = ReadonlyArray<{ c: string; n: number }>;
type GeoPulseListener = (pulse: GeoPulse) => void;
const geoPulseListeners = new Set<GeoPulseListener>();

/**
 * Fires when a sync carries country deltas — someone, somewhere, just popped.
 * Live-connection only: the HTTP fallback deliberately never pulses.
 */
export function onGeoPulse(fn: GeoPulseListener): () => void {
  geoPulseListeners.add(fn);
  return () => geoPulseListeners.delete(fn);
}

let started = false;
let ws: WebSocket | null = null;
let pending = 0;
let sendTimer: ReturnType<typeof setTimeout> | null = null;
let keepaliveTimer: ReturnType<typeof setInterval> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let failures = 0;
let fallbackPostTimer: ReturnType<typeof setInterval> | null = null;
let fallbackPollTimer: ReturnType<typeof setInterval> | null = null;

type Status = "offline" | "connecting" | "open" | "fallback";

function setStatus(s: Status): void {
  dbg.ws = s;
  usePopStore.getState().setConnection(s);
}

function wsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/ws`;
}

export function queuePops(n: number): void {
  pending = Math.min(pending + n, MAX_OFFLINE_BUFFER);
  if (sendTimer === null) {
    sendTimer = setTimeout(sendNow, CLIENT_BATCH_MS);
  }
}

function sendNow(): void {
  sendTimer = null;
  if (pending <= 0) return;
  if (ws !== null && ws.readyState === WebSocket.OPEN) {
    const n = Math.min(pending, MAX_BATCH);
    try {
      ws.send(encodePops(n));
      pending -= n;
    } catch {
      /* send failed — keep pending for retry */
    }
  }
  // Anything left (buffer > MAX_BATCH, or socket down) drains on the next tick
  // or on reconnect. HTTP fallback drains it on its own cadence.
  if (pending > 0 && ws !== null && ws.readyState === WebSocket.OPEN && sendTimer === null) {
    sendTimer = setTimeout(sendNow, CLIENT_BATCH_MS);
  }
}

function connect(): void {
  if (ws !== null || reconnectTimer !== null) return;
  setStatus("connecting");
  let socket: WebSocket;
  try {
    socket = new WebSocket(wsUrl());
  } catch {
    onConnectionLost();
    return;
  }
  ws = socket;

  socket.onopen = () => {
    failures = 0;
    stopFallback();
    setStatus("open");
    if (keepaliveTimer === null) {
      keepaliveTimer = setInterval(() => {
        if (ws !== null && ws.readyState === WebSocket.OPEN) {
          try {
            ws.send("ping");
          } catch {
            /* ignored */
          }
        }
      }, KEEPALIVE_MS);
    }
    if (pending > 0 && sendTimer === null) {
      sendTimer = setTimeout(sendNow, 100);
    }
  };

  socket.onmessage = (ev) => {
    if (typeof ev.data !== "string" || ev.data === "pong") return;
    const msg = parseServerMsg(ev.data);
    if (msg === null) return;
    if (msg.t === "init" || msg.t === "sync") {
      usePopStore.getState().syncTotal(msg.total, msg.live);
      if (msg.t === "init" && msg.c) usePopStore.getState().setMyCountry(msg.c);
      if (msg.t === "sync" && msg.d) {
        const pulse = Object.entries(msg.d)
          .map(([c, n]) => ({ c, n }))
          .sort((a, b) => b.n - a.n);
        for (const fn of geoPulseListeners) fn(pulse);
      }
    } else if (msg.t === "m") {
      const store = usePopStore.getState();
      store.syncTotal(msg.total, store.live);
      for (const fn of milestoneListeners) fn(msg.threshold, msg.total);
    }
    // "slow" is informational — our batcher already respects the limits.
  };

  socket.onclose = () => {
    if (ws === socket) onConnectionLost();
  };
  socket.onerror = () => {
    if (ws === socket) onConnectionLost();
  };
}

function onConnectionLost(): void {
  if (ws !== null) {
    ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
    try {
      ws.close();
    } catch {
      /* already closed */
    }
    ws = null;
  }
  if (keepaliveTimer !== null) {
    clearInterval(keepaliveTimer);
    keepaliveTimer = null;
  }
  failures++;
  if (failures >= 2) startFallback();
  setStatus(fallbackPollTimer !== null ? "fallback" : "offline");

  const backoff = BACKOFF_S[Math.min(failures - 1, BACKOFF_S.length - 1)] * 1000;
  const jitter = backoff * 0.3 * (Math.random() * 2 - 1);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, backoff + jitter);
}

function startFallback(): void {
  if (fallbackPollTimer !== null) return;
  setStatus("fallback");
  fallbackPollTimer = setInterval(async () => {
    const res = await getCount();
    if (res !== null) usePopStore.getState().syncTotal(res.total, res.live);
  }, FALLBACK_POLL_MS);
  fallbackPostTimer = setInterval(async () => {
    if (pending <= 0) return;
    const n = Math.min(pending, MAX_BATCH);
    pending -= n; // optimistic — on failure these pops merge back below
    const total = await postPops(n);
    if (total === null) {
      pending = Math.min(pending + n, MAX_OFFLINE_BUFFER);
    } else {
      usePopStore.getState().syncTotal(total, usePopStore.getState().live);
    }
  }, FALLBACK_POST_MS);
}

function stopFallback(): void {
  if (fallbackPollTimer !== null) {
    clearInterval(fallbackPollTimer);
    fallbackPollTimer = null;
  }
  if (fallbackPostTimer !== null) {
    clearInterval(fallbackPostTimer);
    fallbackPostTimer = null;
  }
}

function retryNow(): void {
  if (ws !== null && ws.readyState === WebSocket.OPEN) return;
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  failures = Math.min(failures, 1); // fresh signal — shrink the backoff
  connect();
}

/** Idempotent. Called when the playground mounts. */
export function startPopLink(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  connect();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") retryNow();
  });
  window.addEventListener("online", retryNow);
}
